import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  Plus, Search, Loader2, RefreshCw, DollarSign, Trash2, X, CheckCircle2, AlertCircle, Download,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination, type PageMeta } from '@/components/Pagination';
import {
  apiCall, formatCents, dollarsToCents, getApiBase,
  type Member, type MemberStatus, type Payment,
} from '@/lib/api';
import { usePermission } from '@/hooks/usePermissions';
import { wiqIdsFromNotes, wiqProfileUrl } from '@/lib/utils';

const STATUS_TABS: Array<{ label: string; value: MemberStatus | 'all' }> = [
  { label: 'All',       value: 'all' },
  { label: 'Active',    value: 'active' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Inactive',  value: 'inactive' },
];

export default function Members() {
  const { getToken } = useAuth();
  const canWrite  = usePermission('members:write');
  const canDelete = usePermission('members:delete');
  const canMerge  = usePermission('members:merge');
  const canExport = usePermission('members:export');

  const [members, setMembers] = useState<Member[]>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta | null>(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<MemberStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-row UI state
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [logFor, setLogFor] = useState<Member | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [exporting, setExporting] = useState(false);

  async function doExport() {
    setExporting(true);
    try {
      const token = await getToken();
      const url = new URL(`${getApiBase()}/v1/members/export.csv`);
      if (status !== 'all') url.searchParams.set('status', status);
      if (search) url.searchParams.set('search', search);
      const init: RequestInit = {};
      if (token) init.headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(url.toString(), init);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = `members-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await apiCall<{ items: Member[] } & PageMeta>('/v1/members', {
        bearerToken: token,
        query: {
          status: status === 'all' ? undefined : status,
          search: search || undefined,
          page,
          per_page: 50,
        },
      });
      setMembers(data.items);
      setPageMeta({ page: data.page, per_page: data.per_page, total: data.total, total_pages: data.total_pages });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [getToken, status, search, page]);

  useEffect(() => { void load(); }, [load]);

  // Reset to page 1 when the filter changes (independent of `load` which refires).
  useEffect(() => { setPage(1); }, [status, search]);

  const summary = useMemo(() => {
    const overdue = members.filter((m) => m.status === 'active' && m.monthly_dues_cents > 0 && !m.is_paid_this_month).length;
    const owed = members
      .filter((m) => m.status === 'active' && m.monthly_dues_cents > 0 && !m.is_paid_this_month)
      .reduce((s, m) => s + m.monthly_dues_cents, 0);
    return { overdue, owed };
  }, [members]);

  async function doDelete(id: string) {
    const token = await getToken();
    await apiCall(`/v1/members/${id}`, { method: 'DELETE', bearerToken: token });
    setMembers((prev) => prev.filter((m) => m.id !== id));
    setPendingDelete(null);
  }

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Members</h1>
          <p className="text-sm text-muted-foreground">
            Roster of cash-paying members.
            {summary.overdue > 0 && (
              <span className="ml-2 text-destructive">
                · {summary.overdue} overdue ({formatCents(summary.owed)} this month)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
          {canExport && (
            <Button variant="outline" size="sm" onClick={() => void doExport()} disabled={exporting || members.length === 0}>
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Export CSV
            </Button>
          )}
          {canMerge && (
            <Button variant="outline" size="sm" asChild>
              <Link to="/dashboard/members/dupes">Duplicates</Link>
            </Button>
          )}
          <Button variant="outline" size="sm" asChild>
            <Link to="/dashboard/cash">Cash payments</Link>
          </Button>
          {canWrite && (
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="mr-1 h-4 w-4" /> Add member
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border bg-background pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="flex gap-1">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setStatus(t.value)}
              className={
                'rounded-full px-3 py-1 text-xs font-medium transition-colors ' +
                (status === t.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent')
              }
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <Card><CardContent className="py-4 text-sm text-destructive">{error}</CardContent></Card>}

      {!error && !loading && members.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No members yet. <button className="underline" onClick={() => setShowCreate(true)}>Add the first one.</button>
          </CardContent>
        </Card>
      )}

      {members.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium text-right">Dues / mo</th>
                  <th className="px-3 py-2 font-medium text-right">Days left</th>
                  <th className="px-3 py-2 font-medium text-right" title="Unused class passes in WrestlingIQ">Passes</th>
                  <th className="px-3 py-2 font-medium">This month</th>
                  <th className="px-3 py-2 font-medium">Last payment</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => {
                  const overdue = m.status === 'active' && m.monthly_dues_cents > 0 && !m.is_paid_this_month;
                  const isPendingDel = pendingDelete === m.id;
                  return (
                    <tr key={m.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="px-3 py-2">
                        <Link to={`/dashboard/members/${m.id}`} className="font-medium hover:underline">{m.name}</Link>
                        {wiqIdsFromNotes(m.notes).map((id) => (
                          <a key={id} href={wiqProfileUrl(id)} target="_blank" rel="noreferrer"
                            className="ml-2 text-xs text-muted-foreground hover:text-foreground hover:underline"
                            title={`Open WrestlingIQ profile ${id}`}>WIQ ↗</a>
                        ))}
                        {(m.email || m.phone) && (
                          <div className="text-xs text-muted-foreground">
                            {[m.email, m.phone].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={m.status === 'active' ? 'default' : m.status === 'suspended' ? 'secondary' : 'outline'}>
                          {m.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {m.monthly_dues_cents > 0 ? formatCents(m.monthly_dues_cents) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {m.balance_days === null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <span className={m.balance_days <= 0 ? 'text-destructive font-medium' : m.balance_days <= 2 ? 'text-amber-600 font-medium' : ''}>
                            {m.balance_days}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {m.available_class_passes > 0
                          ? <span className="font-medium">{m.available_class_passes}</span>
                          : <span className="text-muted-foreground">0</span>}
                      </td>
                      <td className="px-3 py-2">
                        {m.is_paid_this_month ? (
                          <span className="inline-flex items-center gap-1 text-green-700 text-xs">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Paid {formatCents(m.paid_this_month_cents)}
                          </span>
                        ) : overdue ? (
                          <span className="inline-flex items-center gap-1 text-destructive text-xs">
                            <AlertCircle className="h-3.5 w-3.5" /> Owes {formatCents(m.monthly_dues_cents)}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {m.last_payment_at ? (
                          <>
                            <span className="tabular-nums">{formatCents(m.last_payment_cents)}</span>
                            <br />
                            <span title={m.last_payment_at}>{m.last_payment_at.split(' ')[0]}</span>
                          </>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isPendingDel ? (
                          <div className="inline-flex items-center gap-1">
                            <span className="text-xs text-muted-foreground">Delete?</span>
                            <Button variant="destructive" size="sm" className="h-7 px-2" onClick={() => void doDelete(m.id)}>Yes</Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setPendingDelete(null)}><X className="h-3.5 w-3.5" /></Button>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1">
                            {canWrite && (
                              <Button size="sm" variant="outline" className="h-7" onClick={() => setLogFor(m)}>
                                <DollarSign className="h-3.5 w-3.5 mr-1" /> Log payment
                              </Button>
                            )}
                            {canDelete && (
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-muted-foreground hover:text-destructive" onClick={() => setPendingDelete(m.id)} aria-label="Delete member">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Pagination meta={pageMeta} onPageChange={setPage} itemLabel="members" />

      {/* Create-member modal */}
      {showCreate && (
        <AddMemberModal
          onClose={() => setShowCreate(false)}
          onCreated={(m) => { setMembers((prev) => [m, ...prev]); setShowCreate(false); }}
        />
      )}

      {/* Log-payment modal */}
      {logFor && (
        <LogPaymentModal
          member={logFor}
          onClose={() => setLogFor(null)}
          onLogged={() => { setLogFor(null); void load(); }}
        />
      )}
    </div>
  );
}

// ── Add Member modal ────────────────────────────────────────────────────────
function AddMemberModal({ onClose, onCreated }: { onClose: () => void; onCreated: (m: Member) => void }) {
  const { getToken } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [duesStr, setDuesStr] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setErr(null);
    try {
      const token = await getToken();
      const m = await apiCall<Member>('/v1/members', {
        method: 'POST',
        bearerToken: token,
        body: {
          name: name.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          monthly_dues_cents: dollarsToCents(duesStr),
          notes: notes.trim() || null,
        },
      });
      onCreated(m);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} title="Add member">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name" required>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} required autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
          </Field>
          <Field label="Phone">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
          </Field>
        </div>
        <Field label="Monthly dues (USD)">
          <input
            inputMode="decimal"
            placeholder="0.00"
            value={duesStr}
            onChange={(e) => setDuesStr(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputClass + ' resize-none'} />
        </Field>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!name.trim() || submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Log Payment modal ───────────────────────────────────────────────────────
function LogPaymentModal({ member, onClose, onLogged }: { member: Member; onClose: () => void; onLogged: () => void }) {
  const { getToken } = useAuth();
  const onVisitPlan = member.balance_days !== null;
  const defaultMonth = new Date().toISOString().slice(0, 7);
  const [mode, setMode] = useState<'days' | 'month'>(onVisitPlan ? 'days' : 'month');
  const [amountStr, setAmountStr] = useState(member.monthly_dues_cents ? (member.monthly_dues_cents / 100).toFixed(2) : '');
  const [method, setMethod] = useState<'cash' | 'card' | 'transfer' | 'other'>('cash');
  const [coversMonth, setCoversMonth] = useState(defaultMonth);
  const [daysAdded, setDaysAdded] = useState('30');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const amount_cents = dollarsToCents(amountStr);
    if (amount_cents <= 0) { setErr('Amount must be greater than zero'); return; }
    let days: number | null = null;
    if (mode === 'days') {
      days = parseInt(daysAdded, 10);
      if (!Number.isFinite(days) || days <= 0) { setErr('Days must be > 0'); return; }
    }
    setSubmitting(true);
    setErr(null);
    try {
      const token = await getToken();
      await apiCall<Payment>('/v1/payments', {
        method: 'POST',
        bearerToken: token,
        body: {
          member_id: member.id,
          amount_cents,
          method,
          covers_month: mode === 'month' ? (coversMonth || null) : null,
          days_added:   mode === 'days'  ? days : null,
          notes: notes.trim() || null,
        },
      });
      onLogged();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} title={`Log payment — ${member.name}`}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-2 p-1 bg-muted rounded-md">
          <button
            type="button" onClick={() => setMode('days')}
            className={'rounded px-2 py-1.5 text-sm font-medium ' + (mode === 'days' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground')}
          >Adds days</button>
          <button
            type="button" onClick={() => setMode('month')}
            className={'rounded px-2 py-1.5 text-sm font-medium ' + (mode === 'month' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground')}
          >Covers a month</button>
        </div>
        <Field label="Amount (USD)" required>
          <input
            inputMode="decimal"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            className={inputClass}
            required
            autoFocus
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Method">
            <select value={method} onChange={(e) => setMethod(e.target.value as typeof method)} className={inputClass}>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="transfer">Transfer</option>
              <option value="other">Other</option>
            </select>
          </Field>
          {mode === 'days' ? (
            <Field label="Days added">
              <input type="number" min="1" max="3650" value={daysAdded} onChange={(e) => setDaysAdded(e.target.value)} className={inputClass} />
            </Field>
          ) : (
            <Field label="Covers month">
              <input type="month" value={coversMonth} onChange={(e) => setCoversMonth(e.target.value)} className={inputClass} />
            </Field>
          )}
        </div>
        {mode === 'days' && onVisitPlan && (
          <p className="text-xs text-muted-foreground">
            Current balance: {member.balance_days} day{member.balance_days === 1 ? '' : 's'}.
            After this: {(member.balance_days ?? 0) + (parseInt(daysAdded, 10) || 0)}.
          </p>
        )}
        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputClass + ' resize-none'} />
        </Field>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Record
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Tiny shared modal + form primitives ─────────────────────────────────────
const inputClass = 'w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-muted-foreground mb-1">
        {label}{required && <span className="text-destructive"> *</span>}
      </div>
      {children}
    </label>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-card text-card-foreground rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="text-base font-semibold">{title}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
